/**
 * Route Rules System
 * Defines WR route behaviors with waypoints and timing data
 * Requirements: 6.1, 6.2, 6.4
 */

import { RouteDefinition, Position, Waypoint } from '../types';

/**
 * Starting position for the WR (line of scrimmage)
 * Coordinate system: x = lateral position, y = downfield (lower y = further downfield/toward top)
 * WR starts at bottom of field (y=85), on the left side (x=20)
 */
const WR_START_POSITION: Position = { x: 20, y: 85 };

/**
 * Registry of all route definitions
 * Uses a mutable record to support extensibility (Req 6.4)
 * 
 * Coordinate system (vertical orientation):
 * - x: lateral position (0=left sideline, 100=right sideline)
 * - y: downfield position (100=line of scrimmage at bottom, 0=endzone at top)
 */
const routeDefinitions: Record<string, RouteDefinition> = {
  slant: {
    id: 'slant',
    name: 'Slant',
    description: 'Quick inside route at a 60-degree angle',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 75 }, timing: 350, speed: 1 },
      { position: { x: 75, y: 40 }, timing: 2700, speed: 1 },
    ],
    totalDuration: 2700,
  },
  out: {
    id: 'out',
    name: 'Out',
    description: 'Quick 5-yard out route toward the sideline',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 70 }, timing: 500, speed: 1 },
      { position: { x: 5, y: 70 }, timing: 1000, speed: 1 },
    ],
    totalDuration: 1000,
  },
  deepOut: {
    id: 'deepOut',
    name: 'Deep Out',
    description: 'Deep route breaking toward the sideline',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 45 }, timing: 1350, speed: 1 },
      { position: { x: 5, y: 45 }, timing: 1850, speed: 1 },
    ],
    totalDuration: 1850,
  },
  deepIn: {
    id: 'deepIn',
    // Coaches call this a Dig. The id stays `deepIn` so no saved play or coverage rule changes.
    name: 'Dig',
    description: '10-yard route breaking inside across the field',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 45 }, timing: 1350, speed: 1 },
      { position: { x: 60, y: 45 }, timing: 2700, speed: 1 },
    ],
    totalDuration: 2700,
  },
  comeback: {
    id: 'comeback',
    name: 'Comeback',
    description: 'Deep route with a turn back toward the QB',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 25 }, timing: 2000, speed: 1 },
      { position: { x: 15, y: 40 }, timing: 2550, speed: 1 },
    ],
    totalDuration: 2550,
  },
  post: {
    id: 'post',
    name: 'Post',
    description: '10-yard route breaking inside at an angle toward the goalpost',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 55 }, timing: 1000, speed: 1 },
      // 50 units of break-and-climb. At 1400ms this ran 35.7 u/s — faster than any defender can
      // move (33), making the post uncoverable by construction. 1700ms puts it at 29.4.
      { position: { x: 50, y: 15 }, timing: 2700, speed: 1 },
    ],
    totalDuration: 2700,
  },
  corner: {
    id: 'corner',
    name: 'Corner',
    description: '10-yard route breaking outside toward the corner of the endzone',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 55 }, timing: 1000, speed: 1 },
      { position: { x: 5, y: 15 }, timing: 2400, speed: 1 },
    ],
    totalDuration: 2400,
  },
  go: {
    id: 'go',
    name: 'Go',
    description: 'Straight vertical route downfield',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 5 }, timing: 2700, speed: 1 },
    ],
    totalDuration: 2700,
  },
  drag: {
    id: 'drag',
    name: 'Drag',
    description: 'Shallow cross route running across the field',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 80 }, timing: 200, speed: 1 },
      { position: { x: 70, y: 75 }, timing: 1800, speed: 1 },
    ],
    totalDuration: 1800,
  },
  hitch: {
    id: 'hitch',
    name: 'Hitch',
    description: '5-yard route turning back toward the QB',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 70 }, timing: 500, speed: 1 },
      { position: { x: 22, y: 73 }, timing: 700, speed: 1 },
    ],
    totalDuration: 700,
  },
  curl: {
    id: 'curl',
    name: 'Curl',
    description: '10-yard route turning back toward the QB',
    waypoints: [
      { position: { x: 20, y: 85 }, timing: 0, speed: 0 },
      { position: { x: 20, y: 55 }, timing: 1000, speed: 1 },
      { position: { x: 22, y: 60 }, timing: 1300, speed: 1 },
    ],
    totalDuration: 1300,
  },
};

/**
 * Starting position for the RB
 * RB starts to the right of the QB
 */
const RB_START_POSITION: Position = { x: 55, y: 92 };

/**
 * Registry of RB route definitions
 */
const rbRouteDefinitions: Record<string, RouteDefinition> = {
  out: {
    id: 'out',
    name: 'Out',
    description: 'RB runs 5 yards upfield then breaks out toward the sideline',
    waypoints: [
      { position: { x: 55, y: 92 }, timing: 0, speed: 0 },
      { position: { x: 55, y: 77 }, timing: 500, speed: 1 },
      // 30 units across the formation. 700ms made this 42.9 u/s (14.3 yd/s — faster than the
      // 100m world record). 1000ms puts it at 30.
      { position: { x: 85, y: 77 }, timing: 1500, speed: 1 },
    ],
    totalDuration: 1500,
  },
  swing: {
    id: 'swing',
    name: 'Swing',
    description: 'RB swings out to the flat toward the sideline',
    waypoints: [
      { position: { x: 55, y: 92 }, timing: 0, speed: 0 },
      // Retimed off the 42.4 u/s original — see the note on `wheel`.
      { position: { x: 75, y: 85 }, timing: 700, speed: 1 },
      { position: { x: 90, y: 80 }, timing: 1250, speed: 1 },
    ],
    totalDuration: 1250,
  },
  curl: {
    id: 'curl',
    name: 'Curl',
    description: 'RB runs 5 yards upfield then turns around',
    waypoints: [
      { position: { x: 55, y: 92 }, timing: 0, speed: 0 },
      { position: { x: 55, y: 77 }, timing: 500, speed: 1 },
      { position: { x: 52, y: 80 }, timing: 800, speed: 1 },
    ],
    totalDuration: 800,
  },
  wheel: {
    id: 'wheel',
    name: 'Wheel',
    description: 'RB swings to the flat then turns upfield on a go route',
    waypoints: [
      { position: { x: 55, y: 92 }, timing: 0, speed: 0 },
      // ALL THREE segments were superhuman (42.4 / 37.3 / 41.2 u/s). Retimed to ~30, which is
      // the band every WR route already sits in. This lengthens the route from 2500ms to
      // 3420ms — a wheel really does take that long to develop.
      { position: { x: 75, y: 85 }, timing: 700, speed: 1 },
      { position: { x: 85, y: 80 }, timing: 1080, speed: 1 },
      { position: { x: 85, y: 10 }, timing: 3420, speed: 1 },
    ],
    totalDuration: 3420,
  },
};

/**
 * Get all available route types
 * Returns both built-in and dynamically added routes
 */
export function getAvailableRoutes(): string[] {
  return Object.keys(routeDefinitions);
}

/**
 * Get route definition by type
 * Works with both built-in RouteType and custom route IDs
 */
export function getRouteDefinition(routeType: string): RouteDefinition | undefined {
  return routeDefinitions[routeType];
}

/**
 * Get waypoints for a specific route
 * Returns undefined if route doesn't exist
 */
export function getRouteWaypoints(routeType: string): Waypoint[] {
  const route = routeDefinitions[routeType];
  return route ? route.waypoints : [];
}

/**
 * Get total duration for a route animation
 * Returns 0 if route doesn't exist
 */
export function getRouteDuration(routeType: string): number {
  const route = routeDefinitions[routeType];
  return route ? route.totalDuration : 0;
}

/**
 * Get the starting position for the WR
 */
export function getWRStartPosition(): Position {
  return { ...WR_START_POSITION };
}

/**
 * Get the starting position for the RB
 */
export function getRBStartPosition(): Position {
  return { ...RB_START_POSITION };
}

/**
 * Get all available RB route types
 */
export function getAvailableRBRoutes(): string[] {
  return Object.keys(rbRouteDefinitions);
}

/**
 * Get RB route definition by type
 */
export function getRBRouteDefinition(routeType: string): RouteDefinition | undefined {
  return rbRouteDefinitions[routeType];
}

/**
 * Get waypoints for a specific RB route
 */
export function getRBRouteWaypoints(routeType: string): Waypoint[] {
  const route = rbRouteDefinitions[routeType];
  return route ? route.waypoints : [];
}

/**
 * Get total duration for an RB route animation
 */
export function getRBRouteDuration(routeType: string): number {
  const route = rbRouteDefinitions[routeType];
  return route ? route.totalDuration : 0;
}

/**
 * Check if an RB route type is valid
 */
export function isValidRBRoute(routeType: string): boolean {
  return routeType in rbRouteDefinitions;
}

/**
 * Check if a route type is valid (exists in the registry)
 * Works with both built-in RouteType and custom route IDs
 */
export function isValidRoute(routeType: string): boolean {
  return routeType in routeDefinitions;
}

/**
 * Validates a route definition has all required fields
 * Returns true if valid, false otherwise
 */
export function validateRouteDefinition(route: RouteDefinition): boolean {
  if (!route.id || typeof route.id !== 'string' || route.id.trim() === '') {
    return false;
  }
  if (!route.name || typeof route.name !== 'string') {
    return false;
  }
  if (!route.description || typeof route.description !== 'string') {
    return false;
  }
  if (!Array.isArray(route.waypoints) || route.waypoints.length === 0) {
    return false;
  }
  if (typeof route.totalDuration !== 'number' || route.totalDuration <= 0) {
    return false;
  }
  
  // Validate each waypoint
  for (const waypoint of route.waypoints) {
    if (!waypoint.position || typeof waypoint.position.x !== 'number' || typeof waypoint.position.y !== 'number') {
      return false;
    }
    if (typeof waypoint.timing !== 'number' || waypoint.timing < 0) {
      return false;
    }
    if (typeof waypoint.speed !== 'number') {
      return false;
    }
  }
  
  return true;
}

/**
 * Add a new route to the system
 * Requirements: 6.4 - Route system extensibility
 * 
 * @param route - The route definition to add
 * @returns true if route was added successfully, false if validation failed or route already exists
 */
export function addRoute(route: RouteDefinition): boolean {
  // Validate the route definition
  if (!validateRouteDefinition(route)) {
    console.error(`Invalid route definition: ${route.id}`);
    return false;
  }
  
  // Check if route already exists
  if (routeDefinitions[route.id]) {
    console.warn(`Route '${route.id}' already exists. Use updateRoute to modify existing routes.`);
    return false;
  }
  
  // Add the route to the registry
  routeDefinitions[route.id] = route;
  return true;
}

/**
 * Update an existing route in the system
 * Requirements: 6.4 - Route system extensibility
 * 
 * @param route - The route definition to update
 * @returns true if route was updated successfully, false if validation failed or route doesn't exist
 */
export function updateRoute(route: RouteDefinition): boolean {
  // Validate the route definition
  if (!validateRouteDefinition(route)) {
    console.error(`Invalid route definition: ${route.id}`);
    return false;
  }
  
  // Check if route exists
  if (!routeDefinitions[route.id]) {
    console.warn(`Route '${route.id}' does not exist. Use addRoute to create new routes.`);
    return false;
  }
  
  // Update the route in the registry
  routeDefinitions[route.id] = route;
  return true;
}

/**
 * Remove a route from the system
 * Requirements: 6.4 - Route system extensibility
 * 
 * @param routeId - The ID of the route to remove
 * @returns true if route was removed, false if route doesn't exist or is a built-in route
 */
export function removeRoute(routeId: string): boolean {
  // Prevent removal of built-in routes
  const builtInRoutes = ['slant', 'out', 'deepOut', 'deepIn', 'comeback', 'post', 'corner', 'go'];
  if (builtInRoutes.includes(routeId)) {
    console.warn(`Cannot remove built-in route: ${routeId}`);
    return false;
  }
  
  if (!routeDefinitions[routeId]) {
    console.warn(`Route '${routeId}' does not exist.`);
    return false;
  }
  
  delete routeDefinitions[routeId];
  return true;
}

/**
 * Get the count of registered routes
 * Useful for testing extensibility
 */
export function getRouteCount(): number {
  return Object.keys(routeDefinitions).length;
}

export { routeDefinitions, rbRouteDefinitions, WR_START_POSITION, RB_START_POSITION };
